import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router";

import * as credit from "../../../constants/credit";
import * as plan from "../../../constants/plan";
import * as apiUtil from "../../../constants/apiUtil";
import * as cv from "../../../constants/cv";
import * as campaign from "../../../constants/campaign";
import * as error from "../../../constants/error";

import * as actions from "./actions";
import * as creditActions from "../credit/actions";
import * as couponActions from "../../../common/status/coupon/actions";
import { loading, overseasError } from "../../status/actions";

import * as contractif from "../../../middleware/contractif";
import * as eventif from "../../../middleware/eventif";

import ErrorFrontOne from "../../../components/dialog/ErrorFrontOne";

import * as creditUtil from "../credit/creditUtils";
import { withdrawal } from "../../../components/dialog/Withdrawal";
import WarningFront from "../../../components/dialog/WarningFront";
import WarningFrontTwo from "../../../components/dialog/WarningFrontTwo";
import { saveCampaignInfo } from "../../status/localCredit/actions";

/**
 * 会員プラン選択画面.
 */
const PlanSelect = () => {
    /** Hooks. */
    const dispatch = useDispatch();
    const location = useLocation();

    /** 利用規約DOM. */
    const sentenceBoxRef = useRef();
    /** 無料会員DOM. */
    const freeRef = useRef();
    /** スタンダード会員DOM. */
    const standardRef = useRef();
    /** クーポンチェックボックスDOM. */
    const couponCheckBoxRef = useRef();

    /** 利用規約既読判定. */
    const [isBottom, setBottom] = useState(false);
    /** 選択中のプラン. */
    const [selectedPlan, setSelectedPlan] = useState(plan.NONE);
    /** ダウングレード判定ダイアログ. */
    const [downgradeDialogState, setDowngradeDialogState] = useState({open: false, onAccept: null});
    /** エラーダイアログ */
    const [isCriticalError, setIsCriticalError] = useState(false);
    /** 利用規約チェックボックスDOM. */
    const [isCheckBox, setIsCheckBox] = useState(false);
    /** 入会キャンセル失敗ダイアログ. */
    const [isCancelJoinFailure, setIsCancelJoinFailure] = useState(false);
    /** 無料期間終了確認ダイアログ. */
    const [isEndCampaign, setIsEndCampaign] = useState({open: false, onAccept: null});
    /** クーポンコード入力. */
    const [coupon, setCoupon] = useState("");
    /** クーポン有効性チェックエラーダイアログ */
    const [couponErrorDialogState, setCouponErrorDialogState] = useState({open: false, title: "", content: ""});

    /** 会員プラン. */
    const memberPlan = useSelector(state => state.Member.memberPlan);
    /** 会員プラン有効期限日. */
    const expirationDate = useSelector(state => state.Member.expirationDate);
    /** キャンペーン中判定. */
    const isCampaign = useSelector(state => state.Member.isCampaign);
    /** 会員プランの金額. */
    const planPrice = useSelector(state => state.Member.planPrice);
    /** クレジットカード番号. */
    const creditNumber = useSelector(state => state.Member.creditNumber);
    /** 入会導線判定. */
    const isNotJoin = useSelector(state => state.Plan.isNotJoin);
    /** プレミアムコンテンツ判定. */
    const isPremiumContents = useSelector(state => state.Plan.isPremiumContents);
    /** LPから引き渡されたクーポンコード. */
    const defaultCoupon = useSelector(state => state.Plan.couponCode);
    /** 非会員お知らせ既読マップ. */
    const noneReadMap = useSelector(state => state.NoneNotice.noneReadMap);

    /**
     * 画面描画時の現在のプランを設定する処理.
     */
    const initPlan = () => {
        const items = document.getElementsByClassName("p-popupPlan__selectItem");
        const border = document.getElementsByClassName("p-popupPlan__selectBorder");

        // 要素の取得に失敗.
        if (items.length !== 3 || border.length !== 3) return;

        // 現在のプランを設定.
        switch (memberPlan) {
            case plan.FREE:
                items[2].classList.add("is-noSelect");
                items[2].classList.add("is-current");
                break;
            case plan.STANDARD:
                items[0].classList.add("is-noSelect");
                items[0].classList.add("is-current");
                break;
            case plan.PREMIUM:
                items[1].classList.add("is-noSelect");
                items[1].classList.add("is-current");
                break;
            default:
                break;
        }

        // 選択制限表示を設定.
        if (isPremiumContents) {
            freeRef.current.classList.add("is-noSelect");
            standardRef.current.classList.add("is-noSelect");
            border[1].classList.add("is-active");
            setSelectedPlan(plan.PREMIUM);
        }
    }

    /**
     * 画面描画時に実行する処理.
     */
    useEffect(() => {
        document.body.classList.add("is-fixed");
        initPlan();
    }, [initPlan]);

    /**
     * 無料キャンペーンLPからの遷移時、クーポンコードが入力されていれば自動入力.
     */
    useEffect(() => {
        if (couponCheckBoxRef.current && defaultCoupon && defaultCoupon !== "") {
            couponCheckBoxRef.current.checked = true;
            setCoupon(defaultCoupon);
        }
    }, [defaultCoupon]);

    /**
     * プランを選択した際の処理.
     */
    const selectPlan = (r) => {
        const items = document.getElementsByClassName("p-popupPlan__selectItem");
        const border = document.getElementsByClassName("p-popupPlan__selectBorder");

        // 要素の取得に失敗.
        if (items.length !== 3 || border.length !== 3) return;

        switch (r) {
            case plan.FREE:
                if (items[2].classList.contains("is-noSelect")) return;
                border[2].classList.add("is-active");
                border[0].classList.remove("is-active");
                border[1].classList.remove("is-active");
                setSelectedPlan(plan.FREE);
                break;
            case plan.STANDARD:
                if (items[0].classList.contains("is-noSelect")) return;
                border[1].classList.remove("is-active");
                border[0].classList.add("is-active");
                border[2].classList.remove("is-active");
                setSelectedPlan(plan.STANDARD);
                break;
            case plan.PREMIUM:
                if (items[1].classList.contains("is-noSelect")) return;
                border[0].classList.remove("is-active");
                border[2].classList.remove("is-active");
                border[1].classList.add("is-active");
                setSelectedPlan(plan.PREMIUM);
                break;
            default:
                break;
        }
    }

    /**
     * 会員プラン詳細画面に遷移.
     */
    const showPlanDetali = () => {
        document.body.classList.remove("is-fixed");
        dispatch(actions.openPopupPlanDetail());
    }

    /**
     * スクロール時の処理を実装.
     */
    const handleScroll = (e) => {
        const checkbox = document.getElementsByClassName("c-checkbox-parts");
        // 要素の取得に失敗.
        if (sentenceBoxRef.current === null || checkbox.length !== 1 ) return;

        // 最下部判定.
        const clientHeight = sentenceBoxRef.current.clientHeight;
        const scrollHeight = sentenceBoxRef.current.scrollHeight;
        if (Math.abs(scrollHeight - (clientHeight + e.currentTarget.scrollTop)) <= 5){
            checkbox[0].classList.remove("is-disabled");
            setBottom(true);
        }
    }

    /**
     * 戻るボタン実行時.
     */
    const closePopup = () => {
        document.body.classList.remove("is-fixed");
        dispatch(actions.closePopupPlanSelect());
    };

    /**
     * 入会しないボタン実行時.
     */
    const cancelJoin = () => {
        dispatch(loading(true));
        const body = {
            [contractif.CONTRACT_TYPE]: contractif.LEAVE,
            [contractif.BEFORE_PLAN]: plan.NONE,
            [contractif.CARDLIST_ID]: ""
        };

        contractif.postContractManage(body)
        .then(() => {
            // 契約管理API 成功.

            // 退会を行う.
            withdrawal(dispatch);

            closePopup();
        })
        .catch((err) => {
            const errCode = err[apiUtil.ERROR_CODE] ? err[apiUtil.ERROR_CODE].slice(-3) : "";
            if (errCode === contractif.OVERSEAS_ERROR_CODE) {
                dispatch(overseasError(true));
                closePopup();
            }
            else {
                setIsCancelJoinFailure(true);
            }
        })
        .finally(() => dispatch(loading(false)));
    };

    /**
     * 続けるボタン実行時.
     */
    const purchaseCheck = () => {
        // 非会員の場合は同意チェックを確認.
        if (plan.isNone(memberPlan)) {

            // 同意チェックが付いていない場合は終了.
            if (!isCheckBox) return;
        }

        const postContract = (afterPlan, successCallback) => {
            dispatch(loading(true));
            let contractBody = {
                contractType: contractif.PLAN,
                beforePlan: memberPlan,
                afterPlan: afterPlan,
            };
            // クーポン利用にチェックが付いている場合は、クーポンコードも送信.
            if (couponCheckBoxRef.current && couponCheckBoxRef.current.checked) {
                contractBody = {...contractBody, couponCode: coupon};
            }

            contractif.postContractManage(contractBody)
            .then((res) => {
                // プラン登録・変更のCVを送信.

                // 非会員から無料会員への登録時.
                if (plan.isNone(memberPlan)) {
                    cv.sendPlanCV(cv.SIGN_UP, afterPlan);
                }
                // 有料会員からのダウングレード時.
                else {
                    cv.sendPlanCV(cv.DOWN_SELL, afterPlan);
                }

                creditUtil.updateInfo(dispatch, res, noneReadMap);
                successCallback();
                closePopup();
            }, (err) => {
                // セッションエラーを確認.
                contractif.isSessionError(dispatch, err);

                const errCode = err[apiUtil.ERROR_CODE] ? err[apiUtil.ERROR_CODE].slice(-3) : "";
                if (errCode === contractif.OVERSEAS_ERROR_CODE) {
                    dispatch(overseasError(true));
                    closePopup();
                }
                else {
                    let title = "";
                    let content = "";
                    switch(err[apiUtil.ERROR_CODE]) {
                        // クーポンが無効（存在しないなど）.
                        case contractif.WAR04017:
                            title = error.COUPON_USED_FAILURE_INVALID_TITLE;
                            content = error.COUPON_USED_FAILURE_INVALID_CONTENT;
                            break;

                        // クーポンが有効期間前.
                        case contractif.WAR04018:
                            title = error.COUPON_USED_FAILURE_BEFORE_VALID_TITLE;
                            content = error.COUPON_USED_FAILURE_BEFORE_VALID_CONTENT;
                            break;

                        // クーポンが期限切れ.
                        case contractif.WAR04019:
                            title = error.COUPON_USED_FAILURE_EXPIRED_TITLE;
                            content = error.COUPON_USED_FAILURE_EXPIRED_CONTENT;
                            break;

                        // 円谷クーポンAPIでエラー.
                        case contractif.WAR04020:
                            title = error.COUPON_USED_FAILURE_TITLE;
                            content = error.COUPON_USED_FAILURE_CONTENT;
                            break;

                        // クーポンが他ユーザで使用済み.
                        case contractif.WAR04021:
                            title = error.COUPON_USED_FAILURE_OTHER_TITLE;
                            content = error.COUPON_USED_FAILURE_OTHER_CONTENT;
                            break;

                        // クーポンの使用可能プラン外.
                        case contractif.WAR04024:
                            title = error.COUPON_USED_FAILURE_PLAN_TITLE;
                            content = error.COUPON_USED_FAILURE_PLAN_CONTENT;
                            break;

                        default:
                    }
                    setDowngradeDialogState({...downgradeDialogState, open: false});
                    if (title !== "") {
                        setCouponErrorDialogState({
                            open: true,
                            title: title,
                            content: content
                        });
                    }
                    else {
                        setIsCriticalError(true);
                    }
                }
            })
            .finally(() => dispatch(loading(false)));
        };

        if (couponCheckBoxRef.current && couponCheckBoxRef.current.checked) {
            // クーポン入力欄が空のとき、エラーダイアログ表示.
            if (coupon === "") {
                setCouponErrorDialogState({
                    open: true,
                    title: error.COUPON_USED_FAILURE_REQUIRED_TITLE,
                    content: error.COUPON_USED_FAILURE_REQUIRED_CONTENT
                });
                return;
            }

            dispatch(loading(true));

            // クーポン管理（有効性チェック）API呼び出し.
            const requestBody = {
                [eventif.COUPON]: coupon
            };
            eventif.postCoupon(requestBody).then((res) => {
                // 入力したクーポンコードが有効、かつ対象プランが選択したプランと一致する場合に決済確認に進む.
                if (campaign.isValid(res) && res[eventif.TARGET] === selectedPlan) {
                    // プレミアムプランでスタンダードプランを選択した場合のみ、ダウングレード確認に遷移.
                    if (plan.isStandard(selectedPlan) && plan.isPremium(memberPlan)) {
                        if (isCampaign) {
                            setIsEndCampaign({
                                open: true,
                                onAccept: () => {
                                    creditUtil.goPopupCredit(dispatch, location, creditUtil.PLAN, plan.STANDARD, planPrice[plan.STANDARD], creditNumber !== "");
                                    closePopup();
                                }
                            })
                        } else {
                            // 課金処理は無しのため、決済完了画面を表示.
                            setDowngradeDialogState({
                                open: true,
                                onAccept: () => postContract(plan.STANDARD, () => dispatch(creditActions.openPopupCreditResult(credit.RESERVE_CHANGE, plan.STANDARD, planPrice[plan.STANDARD])))
                            });
                        }
                    }
                    else {
                        dispatch(couponActions.updateCouponCode(coupon));
                        dispatch(saveCampaignInfo(res[campaign.CAMPAIGN_NAME], res[eventif.PERIOD]));
                        creditUtil.goPopupCredit(dispatch, location, creditUtil.PLAN_CAMPAIGN, selectedPlan, planPrice[selectedPlan], creditNumber !== "");
                        closePopup();
                    }
                }
                // 選択したプランとクーポン対象プランが異なる場合.
                else if (res[eventif.TARGET] !== selectedPlan) {
                    setCouponErrorDialogState({
                        open: true,
                        title: error.FREE_CAMPAIGN_SELECT_FAILURE_TITLE,
                        content: error.FREE_CAMPAIGN_SELECT_FAILURE_CONTENT
                    });
                }
                // それ以外はクーポン無効エラーダイアログ表示.
                else {
                    setCouponErrorDialogState({
                        open: true,
                        title: error.COUPON_USED_FAILURE_INVALID_TITLE,
                        content: error.COUPON_USED_FAILURE_INVALID_CONTENT
                    });
                }
            }, (err) => {
                // セッションエラーを確認.
                eventif.isSessionError(dispatch, err);

                let title = "";
                let content = "";
                switch(err[apiUtil.ERROR_CODE]) {
                    // クーポンが無効（存在しないなど）.
                    case eventif.WAR06010:
                        title = error.COUPON_USED_FAILURE_INVALID_TITLE;
                        content = error.COUPON_USED_FAILURE_INVALID_CONTENT;
                        break;

                    // クーポンが有効期間前.
                    case eventif.WAR06011:
                        title = error.COUPON_USED_FAILURE_BEFORE_VALID_TITLE;
                        content = error.COUPON_USED_FAILURE_BEFORE_VALID_CONTENT;
                        break;

                    // クーポンが期限切れ.
                    case eventif.WAR06012:
                        title = error.COUPON_USED_FAILURE_EXPIRED_TITLE;
                        content = error.COUPON_USED_FAILURE_EXPIRED_CONTENT;
                        break;

                    // クーポンが他ユーザで使用済み.
                    case eventif.WAR06013:
                        title = error.COUPON_USED_FAILURE_OTHER_TITLE;
                        content = error.COUPON_USED_FAILURE_OTHER_CONTENT;
                        break;

                    // クーポンを同じユーザで使用済み.
                    case eventif.WAR06014:
                        title = error.COUPON_USED_FAILURE_USED_TITLE;
                        content = error.COUPON_USED_FAILURE_USED_CONTENT;
                        break;

                    default:
                        title = error.COUPON_USED_FAILURE_TITLE;
                        content = error.COUPON_USED_FAILURE_CONTENT;
                }
                setCouponErrorDialogState({
                    open: true,
                    title: title,
                    content: content
                });
            })
            .finally(() => dispatch(loading(false)));
            return;
        }

        switch (selectedPlan) {
            case plan.FREE:
                // プレミアム会員.
                if (plan.isPremium(memberPlan)) {
                    // ダウングレード.
                    if (isCampaign) {
                        // キャンペーン適用中の場合は、即時解約とする.
                        setIsEndCampaign({
                            open: true,
                            onAccept: () => postContract(plan.FREE, () => dispatch(creditActions.openPopupCreditResult(credit.PLAN_CHANGE, plan.FREE, 0)))
                        });
                    } else {
                        // 通常時は、解約予約.
                        setDowngradeDialogState({
                            open: true,
                            onAccept: () => postContract(plan.FREE, () => dispatch(creditActions.openPopupCreditResult(credit.RESERVE_CHANGE, plan.FREE, 0)))
                        });
                    }
                }

                // スタンダード会員.
                else if (plan.isStandard(memberPlan)) {
                    // ダウングレード.
                    if (isCampaign) {
                        // キャンペーン適用中の場合は、即時解約とする.
                        setIsEndCampaign({
                            open: true,
                            onAccept: () => postContract(plan.FREE, () => dispatch(creditActions.openPopupCreditResult(credit.PLAN_CHANGE, plan.FREE, 0)))
                        });
                    } else {
                        // 通常時は、変更予約.
                        setDowngradeDialogState({
                            open: true,
                            onAccept: () => postContract(plan.FREE, () => dispatch(creditActions.openPopupCreditResult(credit.RESERVE_CHANGE, plan.FREE, 0)))
                        });
                    }
                }

                // 非会員.
                else {
                    // 無料会員登録.
                    postContract(plan.FREE, () => dispatch(creditActions.openPopupCreditResult(credit.FREE, plan.FREE, 0)));
                }
                break;
            case plan.STANDARD:
                // キャンペーン中ではないプレミアム会員.
                if (plan.isPremium(memberPlan) && !isCampaign) {
                    // ダウングレード.
                    // 課金処理は無しのため、決済完了画面を表示.
                    setDowngradeDialogState({
                        open: true,
                        onAccept: () => postContract(plan.STANDARD, () => dispatch(creditActions.openPopupCreditResult(credit.RESERVE_CHANGE, plan.STANDARD, planPrice[plan.STANDARD])))
                    });
                }

                // キャンペーン中のプラン変更.
                else if (isCampaign) {
                    setIsEndCampaign({
                        open: true,
                        onAccept: () => {
                            creditUtil.goPopupCredit(dispatch, location, creditUtil.PLAN, plan.STANDARD, planPrice[plan.STANDARD], creditNumber !== "");
                            closePopup();
                        }
                    })
                }

                // 上記以外の会員
                else {
                    creditUtil.goPopupCredit(dispatch, location, creditUtil.PLAN, plan.STANDARD, planPrice[plan.STANDARD], creditNumber !== "");
                    closePopup();
                }
                break;
            case plan.PREMIUM:
                // キャンペーン中のプラン変更.
                if (isCampaign) {
                    setIsEndCampaign({
                        open: true,
                        onAccept: () => {
                            creditUtil.goPopupCredit(dispatch, location, creditUtil.PLAN, plan.PREMIUM, planPrice[plan.PREMIUM], creditNumber !== "");
                            closePopup();
                        }
                    })
                }

                // プレミアム会員プラン決済.
                else {
                    creditUtil.goPopupCredit(dispatch, location, creditUtil.PLAN, plan.PREMIUM, planPrice[plan.PREMIUM], creditNumber !== "");
                    closePopup();
                }
                break;
            default:
                return;
        }
    }

    /**
     * 右クリック時.
     */
    const handleContextMenu = (event) => {
        // 右クリックのメニュー非表示.
        event.preventDefault();
    };

    /**
     * 続けるボタン活性化条件.
     */
    const canPushNext = () => {
        const border = document.getElementsByClassName("p-popupPlan__selectBorder");
        if (border.length !== 3) return false;
        // 非会員以外は現在のプランから変更がない場合は非活性.
        if (!border[0].classList.contains("is-active")
            && !border[1].classList.contains("is-active")
            && !border[2].classList.contains("is-active") ) {
            return false;
        }
        if (plan.isNone(memberPlan)) return isCheckBox;
        return true;
    };

    /**
     * チェックボックス切替.
     */
    const handleCheckBox = (event) => {
        setIsCheckBox(event.target.checked);
    };

    /**
     * チェックボックス切替.
     */
    const handleCoupon = (event) => {
        setCoupon(event.target.value);
    };

    /**
     * ダウングレード確認ダイアログ.
     */
    const downgradeDialog = () => {
        return (
            <div style={{zIndex: 9999}}
                onContextMenu={(event) => handleContextMenu(event)}
            >
                <div className="p-popup p-popup--dialog">
                    <div className="p-popup__box">
                        <div className="p-popup__icon p-popup__icon--dialog">
                            <img src="/assets/images/icon-attention-comp-yellow.png" alt="" />
                        </div>
                        <div className="p-popup__wrap p-popup__wrap--dialog">
                            <div className="p-popup__dtl">
                                <div className="p-popup__ttl">プラン変更予約について</div>
                                <div className="p-popup__txt">
                                    <div>現在のプランの有効期限が終了したあとに新プランへ変更となります。</div>
                                    <br/>
                                    <div>現在のプラン：{plan.EN_TO_JP[memberPlan]}</div>
                                    <div>有効期限：{expirationDate}</div>
                                    <br/>
                                    <div>変更後のプラン：{plan.EN_TO_JP[selectedPlan]}</div>
                                    <div>新プランの開始日：{apiUtil.getChangeDate(expirationDate.replace(/\D/g, "/").slice(0, -1))}</div>
                                </div>
                                <div className="p-popup__btnWrap">
                                    <div className="p-popup__btnItem c-btn c-btn--gray" onClick={() => {closePopup(); setDowngradeDialogState({open: false, onAccept: null});}}>キャンセル</div>
                                    <div className="p-popup__btnItem c-btn c-btn--thinRed" onClick={() => downgradeDialogState.onAccept()}>OK</div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="p-popup__bg"></div>
                </div>
            </div>
        );
    };

    /**
     * 無料キャンペーン終了ダイアログキャンセル.
     */
    const isEndCampaignReject = () => {
        setIsEndCampaign({open: false, onAccept: null});
        closePopup();
    };

    /**
     * 戻るボタン押下時の処理.
     */
    const back = () => {
        closePopup();
    };

    /**
     * 契約管理API失敗時の処理.
     */
    const contractFailure = () => {
        window.location.reload();
        closePopup();
    };

    return (
        <div className="p-popup">
            {/* ダウングレード確認ダイアログ表示 */}
            {downgradeDialogState.open && downgradeDialog()}
            {/* 入会キャンセル失敗ダイアログ表示 */}
            {isCancelJoinFailure && <WarningFront input={{title: "入会キャンセルエラー", content: "入会キャンセルに失敗しました。"}} onAccept={() => {window.location.reload(); setIsCancelJoinFailure(false);}}/>}
            {/* エラーダイアログ表示 */}
            {isCriticalError && <ErrorFrontOne input={{title: "決済失敗", content: "決済に失敗しました。"}} onAccept={contractFailure}/>}
            {/* 無料期間終了確認ダイアログ表示 */}
            {isEndCampaign.open && <WarningFrontTwo input={{title: "キャンペーン適用が終了します", content: "プラン変更するとキャンペーンの適用が終了します。", yes: "OK", no: "キャンセル"}} onAccept={() => isEndCampaign.onAccept()} onReject={isEndCampaignReject}/>}
            {/* クーポン有効性チェック/契約管理（クーポン関連）エラーダイアログ表示 */}
            {couponErrorDialogState.open && <WarningFront input={{title: couponErrorDialogState.title, content: couponErrorDialogState.content}} onAccept={() => setCouponErrorDialogState({open: false, title: "", content: ""})}/>}
            <div className="p-popup__box p-popup__box--largeBox">
                <div className="p-popupBox">
                    <div className="p-popupBoxTtl p-popupBoxTtl--plan">
                        <div className="p-popupBoxTtl__txt">プラン選択</div>
                    </div>
                    <div className="p-popupBoxWrap p-popupBoxWrap--plan p-popupBoxWrap--deepGray">
                        <div className="p-popupPlan">
                            {/* 新規入会時(=プラン未選択状態)にのみ画面上部の説明文を表示. */}
                            {plan.isNone(memberPlan) &&
                                <div className="p-popupPlan__dtl">
                                    TSUBURAYAアカウントとdアカウントの紐付けが完了しました。TSUBURAYA IMAGINATIONに入会するには、プラン選択が必須です。ご希望のプランを選択し、会員規約を確認上、次にお進みください。
                                </div>
                            }
                            <ul className="p-popupPlan__select">
                                {/* スタンダード会員に変更. */}
                                <li
                                    className="p-popupPlan__selectItem"
                                    onClick={() => selectPlan(plan.STANDARD)}
                                    ref={standardRef}
                                >
                                    <div className="p-popupPlan__selectBorder"></div>
                                    <div className="p-popupPlan__selectHead p-popupPlan__selectHead--red">
                                        <div className="p-popupPlan__selectHeadName">
                                            <div className="p-popupPlan__selectHeadTxt">スタンダード</div>
                                        </div>
                                        <div className="p-popupPlan__selectHeadDtl">
                                            <div className="p-popupPlan__selectHeadTxt">
                                                <div className="p-popupPlan__selectHeadDtlFee">月額<span>{Number(planPrice[plan.STANDARD_WITHOUT_TAX]).toLocaleString() + "円"}</span>（税抜）</div>
                                                <div className="p-popupPlan__selectHeadDtlTax">{Number(planPrice[plan.STANDARD]).toLocaleString() + "円（税込）"}</div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="p-popupPlan__selectDtl">
                                        <div className="p-popupPlan__selectDtlTxt">
                                            ウルトラマンシリーズからオリジナル新作まで
                                            <br/><span className="p-popupPlan__selectDtlTxtRed">約1200エピソード</span>が見放題！<br/>コミックスや小説などその他コンテンツ、サービスも多数！
                                        </div>
                                        <div className="p-popupPlan__category">
                                        <span className="p-popupPlan__categoryItem p-popupPlan__categoryItem--thinRed">映像</span>
                                            <span className="p-popupPlan__categoryItem p-popupPlan__categoryItem--thinRed">読み物</span>
                                            <span className="p-popupPlan__categoryItem p-popupPlan__categoryItem--thinRed">イベントチケット先行受付</span>
                                            <span className="p-popupPlan__categoryItem p-popupPlan__categoryItem--thinRed">会員限定グッズ</span>
                                            <span className="p-popupPlan__categoryItem p-popupPlan__categoryItem--thinRed">オンラインイベント</span>
                                        </div>
                                    </div>
                                </li>
                                {/* プレミアム会員に変更. */}
                                <li className="p-popupPlan__selectItem" onClick={() => selectPlan(plan.PREMIUM)}>
                                    <div className="p-popupPlan__selectBorder"></div>
                                    <div className="p-popupPlan__selectHead p-popupPlan__selectHead--gold">
                                        <div className="p-popupPlan__selectHeadName">
                                            <div className="p-popupPlan__selectHeadTxt">プレミアム</div>
                                        </div>
                                        <div className="p-popupPlan__selectHeadDtl">
                                            <div className="p-popupPlan__selectHeadTxt">
                                                <div className="p-popupPlan__selectHeadDtlFee">年額<span>{Number(planPrice[plan.PREMIUM_WITHOUT_TAX]).toLocaleString() + "円"}</span>（税抜）</div>
                                                <div className="p-popupPlan__selectHeadDtlTax">{Number(planPrice[plan.PREMIUM]).toLocaleString() + "円（税込）"}</div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="p-popupPlan__selectDtl">
                                        <div className="p-popupPlan__selectDtlTxt">
                                            スタンダード会員サービスを含む円谷プロ<span className="p-popupPlan__selectDtlTxtRed">作品約1700エピソード</span>が見放題！
                                            <br/>さらに、毎月開催のオンラインイベントへご招待、チケット超先行受付などプレミアムな特典満載。※月額換算 ￥1,815相当（税込）
                                        </div>
                                        <div className="p-popupPlan__category">
                                            <span className="p-popupPlan__categoryItem p-popupPlan__categoryItem--thinRed">映像</span>
                                            <span className="p-popupPlan__categoryItem p-popupPlan__categoryItem--thinRed">読み物</span>
                                            <span className="p-popupPlan__categoryItem p-popupPlan__categoryItem--thinRed">イベントチケット先行受付</span>
                                            <span className="p-popupPlan__categoryItem p-popupPlan__categoryItem--thinRed">会員限定グッズ</span>
                                            <span className="p-popupPlan__categoryItem p-popupPlan__categoryItem--gold">オンラインイベント</span>
                                            <span className="p-popupPlan__categoryItem p-popupPlan__categoryItem--gold">イベントチケット最速先行受付</span>
                                            <span className="p-popupPlan__categoryItem p-popupPlan__categoryItem--gold">プレミアム限定グッズ</span>
                                        </div>
                                    </div>
                                </li>
                                {/* 無料会員に変更. */}
                                <li
                                    className="p-popupPlan__selectItem"
                                    onClick={() => selectPlan(plan.FREE)}
                                    ref={freeRef}
                                >
                                    <div className="p-popupPlan__selectBorder"></div>
                                    <div className="p-popupPlan__selectHead p-popupPlan__selectHead--free">
                                        <div className="p-popupPlan__selectHeadName p-popupPlan__selectHeadName--free">
                                            <div className="p-popupPlan__selectHeadTxt">無料</div>
                                        </div>
                                    </div>
                                    <div className="p-popupPlan__selectDtl p-popupPlan__selectDtl--free">
                                        <div className="p-popupPlan__selectDtlTxt">一部の動画と読み物が視聴可能。</div>
                                    </div>
                                </li>
                                <li className="p-popupPlan__planBtn c-btn c-btn--gray" onClick={() => showPlanDetali()}>各プランについての詳細はこちら</li>
                            </ul>
                            <div className="c-entryBox c-entryBox--plan">
                                <div className="c-entryBox__wrap">
                                    <div className="c-entryBox__head c-entryBox__head--plan">
                                        <div className="c-entryBox__headTxt">クーポンをお持ちの方はこちら</div>
                                    </div>
                                    <div className="c-entryBox__input">
                                        <label className="c-entryBox__checkBox">
                                            <input className="c-entryBox__checkBoxMark" type="checkbox" ref={couponCheckBoxRef}/>
                                            <span className="c-entryBox__checkBoxDummy"></span><span className="c-entryBox__checkBoxTxt" for="coupon">クーポンコードを使う</span>
                                        </label>
                                        <input className="c-entryBox__inputBox c-entryBox__inputBox--plan" type="text" placeholder="例)aBcD123" style={{imeMode: "disabled"}} value={coupon} onChange={handleCoupon}/>
                                    </div>
                                </div>
                            </div>
                            <div className="p-popupPlan__attention">
                                <div className="p-popupPlan__attentionTxt">※スタンダードおよびプレミアムの登録には、決済情報登録が必要です。</div>
                                <div className="p-popupPlan__attentionTxt">※一部の作品は、見放題サービスに含まれていません。</div>
                                <div className="p-popupPlan__attentionTxt">※個別課金でもお楽しみいただけます。</div>
                            </div>
                        </div>
                        {/* 新規登録時に表示. */}
                        {plan.isNone(memberPlan) &&
                            <div className="c-popupMemberShip">
                                <div className="c-popupMemberShip__head">TSUBURAYA IMAGINATION 会員規約</div>
                                <div
                                    className="c-popupMemberShip__sentenceBox"
                                    onScroll={(e) => handleScroll(e)}
                                    ref={sentenceBoxRef}
                                >
                                    <div className="c-popupMemberShip__sentenceTxt">
                                        <div id="trems">本規約は、当社の本サービスの提供条件及び本サービスに関するユーザーと当社との間の権利義務関係を定めることを目的とするものであり、ユーザーと当社との間の本サービスにかかわる一切の事項に適用されるものとします。ユーザーは本規約の全文を確認し、本規約の内容にご同意頂けた場合に限り、本サービスを利用することができます。</div><br/>第１条（定義）<br/>本規約における用語の定義は以下の各号に定める通りとします。<br/>①「本規約」<br/>当社が定める、この「TSUBURAYA IMAGINATION 会員規約」をいいます。<br/>②「本サービス利用契約」<br/>本規約に同意いただいたうえで本サービスの提供を受けることにより、当社と会員の間で締結される本サービスの利用契約をいいます。<br/>③「当社」<br/>本サービスの提供主体である株式会社円谷プロダクション（本店所在地：東京都渋谷区南平台町16番17号）をいいます。<br/>④「有料会員」<br/>第３条（登録）に基づいて登録をすることにより、加入プランに応じた本サービスの提供を受けることができる個人の会員をいいます。<br/>⑤「無料会員」<br/>第３条（登録）に基づいて登録をすることにより、本サービスの提供を受けることができる個人の会員をいいます。<br/>⑥「会員」<br/>有料会員と無料会員の総称をいいます。<br/>⑦「本サービス」<br/>当社が本サイト又は本アプリを通じて提供する、有償又は無償で本コンテンツを閲覧等できるサービス「TSUBURAYA IMAGINATION」（サービスの名称又は内容が変更された場合は、当該変更後のサービスを含むものとします。）をいい、プランとしては、「無料プラン」「スタンダードプラン」「プレミアムプラン」があります。なお、当社が本サービスの提供主体となりますが、株式会社NTTドコモ（以下「ドコモ」といいます。）も本サービスの運営に関与しております。そのため、ドコモから本サービスに関連する広告がなされる場合がございます。<br/>⑧「有料サービス」<br/>「スタンダードプラン」又は「プレミアムプラン」に加入することで、それぞれのプランに応じて利用することができる有償のサービスをいいます。<br/>⑨「本サイト」<br/>そのドメインが「imagination.m-78.jp」である、当社又は当社の外部委託先が運営する、本サービスに係るウェブサイト（当該ウェブサイトのドメイン又は内容が変更された場合は、当該変更後のウェブサイトを含むものとします。）をいいます。<br/>⑩「本アプリ」<br/>当社又は当社の外部委託先が運営する、本サービスに係る通信電子機器端末向けアプリケーションをいいます。<br/>⑪「本コンテンツ」<br/>当社が本サービスとして本サイト又は本アプリを通じて提供する、デジタルデータ化された映像、画像、テキスト、音声及びこれらの一又は二以上の結合物をいいます。<br/><br/>第２条（適用範囲）<br/>1.　本規約は、本サービスの提供条件及び本サービスの利用に関して、当社と会員との間の本サービスの利用に関する一切の関係に適用されます。<br/>2.　当社が本サイト上又は本アプリ上で掲載する本サービスの利用に関するあらゆる条件は、本規約の一部を構成するものとします。<br/>3.　会員が本サービスを利用した場合、当該ユーザーは本規約に同意していただいたものとみなされます。本規約の条件に同意していただけないユーザーは、本サービスを利用することができません。<br/>4.　前項により、会員が本サービスを利用した時点をもって、当社と会員との間において、本サービス利用契約が成立するものとします。<br/><br/>第３条（登録）<br/>1.　会員は、有料会員であると無料会員であるとを問わず、本サイト上又は本アプリ上で当社が案内するところに従い、TSUBURAYAアカウント会員登録とドコモが定める「dアカウント規約」に基づき発行提供する「dアカウント」（以下「dアカウント」といいます。）の取得を行っていただく必要があります。<br/>2.　本コンテンツには、有料のコンテンツと無料のコンテンツとがあり、有料会員は、自らが選択するプランにより、当該プランに応じた有料の本コンテンツを閲覧等することが可能となります。<br/>3.　当社は、以下の場合に本サービスの会員登録を承諾しないことがあります。承諾しなかった場合の判断の理由については一切申込者に開示しません。また、申込者は判断の結果に対して異議を述べることはできません。<br/>（1）ユーザーが規約違反等により当社又はそのグループ会社が運営するサービス等の利用を停止されたことがある場合等、当社が会員登録を承諾すべきではないと判断した場合<br/>（2）当社の日常的な業務の遂行上又は技術上、支障がある場合<br/>（3）その他、当社が不適当と合理的な理由により認めた場合<br/>４.　未成年者が会員登録をする場合は、登録にあたり、事前に法定代理人（親権者）の承諾を得てから会員登録をするものとし、未成年の方からの会員登録が完了した時点で、当社は法定代理人（親権者）の同意があったものとみなします。<br/><br/>第４条（登録事項の変更）<br/>1.　会員は、登録事項に変更が生じた場合、本サイト上に設置するプロフィールより、遅滞なく登録事項を変更するものとします。会員が当該変更の入力を怠るなど、登録事項の変更が適切に行われないために不利益を被ったとしても、当社は一切の責任を負わないものとします。<br/>2.　TSUBURAYAアカウント会員登録事項及びdアカウント登録事項に変更が生じた場合も、前項と同様とし、登録事項の変更は、それぞれの登録をした場所で遅滞なく行っていただくものとします。<br/><br/>第５条（会員番号、ログインID及びパスワードの管理）<br/>1.　会員は、自己の責任において、本サービスに関する会員番号並びに本サービスの利用に必要となるTSUBURAYAアカウントのログインID及びパスワードを適切に管理するものとし、これらを第三者に利用させ、又は譲渡し、貸与し、担保設定するなどの行為をしてはならないものとします。<br/>2.　ログインID又はパスワードの再発行については、本サイト又は本アプリの表示に従うものとします。<br/>3.　会員による会員番号、ログインID及びパスワードの管理不十分、紛失、失念及び使用上の過誤に起因して、又はこれらに関連して生じる会員の不利益又は損害に関する責任は会員が負うものとし、当社は一切の責任を負わないものとします。但し、当社の責めに帰すべき事由による場合はこの限りではありません。<br/><br/>第６条（本サービスの利用）<br/>1.　本サービスは、日本国内に居住する個人のユーザーにより、日本国内でのみ利用可能です。<br/>2.　会員は、会員登録後、加入プランに応じた本サービスの提供を受けることができます。また、いずれの加入プランの会員であっても、当社の商品に付属されている「ウルトラコード」、その他のコードを入力することにより、特定のコンテンツを閲覧することができるサービスがあります。<br/>3.　本サービスを利用するために必要な対応端末、ソフトウェア、電気通信回線等の環境は、会員の負担と責任においてご準備いただくものとします（通信料金は会員の負担となります）。本サービスを快適にご利用いただくための推奨環境をご確認ください。なお、本サービスを利用中の対応端末の破損等について、当社は一切の責任を負いません。<br/>4.　当社は、本サービス内（当社又は当社の委託先が送信するメールマガジンを含みます。）において、広告を掲載する場合があります。当該広告に掲載されるリンク先のサービスは、本サービスとは別のサービスであり、当社は、当該広告及び当該リンク先のサービスについて、いかなる責任も負いません。<br/>5.　当社は、本サービスの内容（本コンテンツを含みます）を定期的に、又は不定期的に更新します。会員は、本サイト又は本アプリにアクセスすることで、加入プランに応じ、その時点における本サービスを利用することができます。<br/><br/>第７条（料金及び支払方法）<br/>{"1.　本サービスのうち、有料となるサービスの利用料金は、スタンダードプラン月額" + Number(planPrice[plan.STANDARD]) + "円（税込）、プレミアムプラン年額" + Number(planPrice[plan.PREMIUM]).toLocaleString() + "円（税込）とします。"}<br/>2.　会員は、本サービス利用の対価として、加入プランに応じた利用料金を、本サイト上又は本アプリ上で表示する方法により当社に支払うものとします。利用料金は日割り計算されませんので、月途中に本サービス利用契約が成立又は終了（登録抹消、退会等その理由の如何を問いません。）した場合でも、スタンダードプランは1か月分、プレミアムプランは1年分の利用料金をお支払いいただきます。<br/>3.　当社は第１項の利用料金を変更することができるものとし、当該変更は将来に向けて効力を有するものとします。この場合、当社は予めその効力発生時期を定め、かつ、利用料金を変更する旨及び変更後の利用料金の額並びにその効力発生時期について、本サイトを通じて公表するものとします。<br/>4.　会員は、変更後の利用料金に同意いただけない場合は、変更が適用される日までに、本規約第12条に基づき本サービスの退会手続きを行ってください。有料会員が変更適用日までに退会手続きを完了していない場合、変更後の利用料金に同意したものとみなされ、変更適用日の属する暦月分から、変更後の利用料金が課金されます。<br/><br/>第８条（禁止事項）<br/>1.　会員は、本サービスの利用又は本コンテンツの閲覧等にあたり、以下の各号のいずれかに該当する行為又は該当すると当社が判断する行為をしてはなりません。<br/>（1）法令に違反する行為又は犯罪行為に関連する行為<br/>（2）当社、本サービスの他の会員その他の第三者に対する詐欺又は脅迫行為<br/>（3）公序良俗に反する行為<br/>（4）当社、本サービスの他の会員その他の第三者の知的財産権、肖像権、プライバシーの権利、名誉、声望、声価、その他の権利又は法律上保護される利益を侵害する行為<br/>（5）本サービスを通じ、以下に該当し、又は該当すると当社が判断する情報を当社又は本サービスの他の会員その他の第三者に送信する行為又は当該行為を扇動する行為<br/>・過度に暴力的又は残虐的な表現を含む情報<br/>・コンピューター・ウィルスその他の有害なコンピューター・プログラムを含む情報<br/>・当社、本サービスの他の会員その他の第三者の名誉又は信用を毀損する表現を含む情報<br/>・過度にわいせつな表現を含む情報<br/>・差別を助長し又はそのおそれのある表現を含む情報<br/>・自殺又は自傷行為を助長し又はそのおそれのある表現を含む情報<br/>・薬物の不適切な利用を助長し又はそのおそれのある表現を含む情報<br/>・反社会的な表現を含む情報<br/>・他人に不快感を与える表現を含む行為<br/>（6）本サービスの運営を妨害し又は妨害するおそれのある行為又は当該行為を扇動する行為<br/>（7）本サービスのネットワーク又はシステム等に過度な負荷をかける行為又は当該行為を扇動する行為<br/>（8）当社のネットワーク又はシステム等に不正にアクセスし、又は不正なアクセスを試みる行為又は当該行為を扇動する行為<br/>（9）本コンテンツの全部又は一部を複製、改変、改ざんし、又は逆コンパイル、逆アセンブル等のリバースエンジニアリング、その他内容を解析して、人間が読み取り可能な形に変換する行為。<br/>（10）第三者に成りすます行為<br/>（11）本サービスの他の会員のID若しくはパスワードを利用する行為又は当社の商品に付属されている「ウルトラコード」、その他のコードのうち、自己のものではないものを利用する行為<br/>（12）当社が事前に承諾しない本サービス上での広告宣伝、勧誘又は営業行為<br/>（13）当社、本サービスの他の利用者その他の第三者に不利益、損害又は不快感を与える行為<br/>（14）本サービスの他の会員に関する情報を収集する行為<br/>（15）本サービスを日本国外で利用する行為<br/>（16）dアカウントを不正に使用する行為<br/>（17）本サービスに付されている著作権表示その他の権利表示を除去し、又は変更する行為<br/>（18）本規約に反する行為<br/>（19）前各号の行為を直接又は関接に惹起し、又は容易にする行為<br/>（20）その他当社が不適切であると判断する行為<br/>2.　前項の各号のいずれかに該当すると当社が判断した場合、当社は当社の裁量の範囲において、これらの行為の停止措置、削除措置、会員のアカウント停止処分又は退会処分を採るものとし、正当な理由がない限り、会員はこれらの措置又は処分に異議を唱えないものとします。<br/><br/>第９条（本サービスの停止等）<br/>1.　当社は、以下のいずれかに該当する場合には、会員に事前に通知又は公表することなく、本サービスの全部又は一部の提供を停止し又は中断することができるものとします。なお、この場合であっても第7条に定める利用料金の減額等は行ないません。<br/>（1）本サービスに係るシステムの点検又は保守作業を緊急に行う場合<br/>（2）ソフトウェア、ネットワーク等が事故により停止した場合<br/>（3）地震、落雷、火災、風水害、疫病の流行その他の天災事変又は停電などの不可抗力により本サービスの運営ができなくなった場合<br/>（4）法令又は規制当局により要請された場合<br/>（5）その他、当社が停止又は中断を必要と判断した場合<br/>2.　当社は、本条に基づき当社が行った措置によって生じる会員の不利益又は損害について、一切の責任を負いません。<br/><br/>第10条（権利帰属）<br/>1.　本サイト、本アプリ及び本サービスに関する知的財産権は、すべて当社又は当社に権利を許諾している第三者に帰属しており、本規約に基づく本サービスの提供は、法令又は本規約に別段の定めがある場合を除き、本サイト、本アプリ又は本サービスに関する当社又は当社に権利を許諾している第三者の知的財産権の利用許諾を意味するものではありません。<br/>2.　会員がお問い合わせフォーム、その他の本サイト又は本アプリの投稿機能等を使用して当社又は他の会員に情報を発信する場合、会員は、当該情報に含まれる画像データ、音声データ、動画データ、テキストデータその他のデータ（以下「会員発信情報」といいます。）について、自らが投稿その他の送信をすることについての適法な権利を有していること及び会員発信情報が第三者の権利を侵害していないことを当社に対し表明し、保証するものとします。<br/>3.　会員は、会員発信情報について、当社に対し、全世界において非独占的な利用権を無償で許諾するものとします。当該利用権は、再許諾及び譲渡が可能なものであり、その許諾期間は当該会員発信情報の送信完了時点から、本サービスが終了するまでの期間とします。<br/>4.　会員は、前項の利用権に関して、当社、当該利用権の当社からの譲受人、承継人その他の当社が指定する者に対し、著作者人格権を行使しないことに同意するものとします。<br/>5.　会員は、当社が会員発信情報の全部又は一部の削除若しくは編集をすることに同意するものとします。<br/><br/>第11条（登録抹消等）<br/>1.　当社は、会員が、以下の各号のいずれかの事由に該当する場合、事前の通知又は催告を要することなく、会員発信情報を削除したうえ、当該会員について本サービスの利用を一時的に停止し、会員としての登録を抹消又は本サービス利用契約を解除することができるものとします。<br/>（1）本規約のいずれかの条項に違反した場合<br/>（2）登録事項に虚偽が含まれていることが判明した場合<br/>（3）支払停止又は支払不能となった場合<br/>（4）その他、当社が本サービスの利用、会員としての登録又は本サービス利用契約の継続を適当でないと正当な理由により判断した場合<br/>2.　前項各号のいずれかに該当した会員は、当社に対して負っている債務のすべてについて、当然に期限の利益を喪失するものとし、直ちに当社に対してすべての債務の履行を行わなければならないものとします。<br/>3.　当社は、本条に基づき当社が行った行為に関して生じる会員の不利益又は損害について、一切の責任を負いません。また、当社が、本サービス利用契約の全部又は一部を解除した場合であっても、当社が会員に対して有する損害賠償請求権その他の法的補償を求める権利に何らの影響を与えないものとします。<br/><br/>第12条（退会）<br/>1.　会員は、本サイト又は本アプリで案内するところに従い、本サービスから退会し、自己の登録を抹消することができます。<br/>2.　無料会員は、退会時点をもって、本サービスを利用することができなくなります。<br/>3.　有料会員は、退会手続きを行なった後、プラン契約期間満了時点で有料会員の資格を喪失し、以後有料サービスは利用できなくなります。退会後、有料サービスのご利用を希望される場合には、再度有料会員登録をしてください。なお、退会された原因によっては、再度の登録の申込みが認められない場合があります。<br/>4.　退会した時点において、会員に当社に対して負っている債務が存在する場合、会員は、当社に対して負っている債務の一切について当然に期限の利益を喪失するものとし、直ちに当社に対してすべての債務の履行を行わなければならないものとします。<br/>5.　退会後の会員情報の取り扱いについても、第16条の定めに従うものとします。<br/>6.　退会に際して、会員に如何なる事情があった場合であっても、当社は返金対応をすることはいたしません。<br/><br/>第13条（本サービスの内容の変更及び終了）<br/>1.　当社は、当社の都合により、会員に対する事前の承諾を得ることなく、本サービスの内容を変更し、又は提供を終了する場合があります。この場合、当社は事前に本サイト又は本アプリを通じて、会員にその旨を公表するものとします。但し、本規約第６条第５項に定める本サービスの内容（本コンテンツを含みます）の更新についてはこの限りではありません。<br/>2.　当社は、本条に基づき当社が行った本サービスの内容の変更又は本サービスの提供の終了によって生じるユーザーの不利益又は損害について、一切の責任を負いません。<br/><br/>第14条（保証の否認及び免責）<br/>1.　当社は、本サービスが会員の特定の目的に適合すること、期待する機能、商品的価値、正確性及び有用性を有すること、会員による本サービスの利用が、会員に適用ある法令又は業界団体の規則等に適合すること、及び不具合が生じないことについて、何等保証するものではありません。<br/>2.　当社は、故意又は重過失による場合を除き、当社による本サービスの提供の中断、停止、終了、利用不能又は変更、会員が本サービスに送信した会員発信情報の削除又は消失、会員登録の抹消、本サービスの利用による登録データの消失又は機器の故障若しくは損傷、その他本サービスに関して会員が被った損害について賠償する責任を一切負わないものとします。<br/>3.　法令その他の理由により当社が責任を負う場合であっても、当社は、有料会員においては、当該有料会員に現実的に発生した直接かつ通常の損害につき、過去1ヶ月間に当該有料会員が当社に支払った対価の金額を超えて賠償する責任を、また無料会員においては、当該無料会員に現実的に発生した直接かつ通常の損害につき、金5,000円を超えて賠償する責任を、それぞれ負わないものとします。但し、有料会員がプレミアムプランに加入されている場合に限り、当社は、当該有料会員については、当該有料会員に現実的に発生した直接かつ通常の損害につき、過去12ヶ月間に当該有料会員が当社に支払った対価の金額を超えて賠償する責任を負わないもの読み替えるものとします。また、特別の事情によって生じた損害であっても、その事情の予見可能性の有無及び予見すべきであったか否かにかかわらず、当社は当該特別の事情によって生じた損害を賠償する責任を負わず、かつ付随的損害、間接損害、将来の損害及び逸失利益に係る損害については賠償する責任を負わないものとします。<br/>4.　本サービス、本サイト又は本アプリに関連して、会員と他の会員又は第三者との間において生じた取引、連絡及び紛争については、当社は一切の責任を負いません。<br/><br/>第15条（秘密保持）<br/>会員は、本サービスに関連して当社が会員に対して秘密に取り扱うことを求めて開示した非公知の情報について、当社の事前の書面による承諾がある場合を除き、厳重に秘密として取り扱うものとします。<br/><br/>第16条（会員情報の取り扱い）<br/>1.　当社は、本サービスに関連して取得した会員の個人情報その他の会員に関するデータ（メンバーシップ及び、認証識別情報、クッキー、IPアドレス、お問い合わせ内容、本コンテンツの視聴履歴その他本サービスの利用状況等を含み、以下「会員データ」といいます。）を当社のプライバシーポリシーに定める利用目的の範囲で利用します。<br/>2.　当社は、本サービスの契約又は解約に係る分析、マーケティング及び会員又は本サービス未契約のユーザーに対する広告、ドコモの本サービス以外の他事業を含む今後の新事業への活用のため、会員データのうち識別子、契約プラン、本サイト閲覧履歴、動画視聴履歴、ECサイトの利用履歴、イベントチケットの購買履歴、当社が会員に実施したアンケートの回答及び入退会日等をドコモに提供します。<br/>3.　会員が当社にお問い合わせいただいた内容が、ドコモサービス又はドコモと会員間の契約に関する場合、当社は、会員のご指示に基づき会員の同意を得た上で、ドコモに会員データの一部を伝える場合があります。<br/>4.　本サイトではドコモが提供する情報収集モジュールを用い、サイト利用者のアクセス履歴等の情報を収集する場合があります。サイト利用者はそのような情報の収集を希望しない場合、これを停止することができます。詳細については、以下のサイトをご覧ください。<a className="c-popupMemberShip__sentenceTxtLink" href="https://www.nttdocomo.co.jp/utility/term/handling_information.html" target="_blank" rel="noreferrer">お客さまが閲覧するwebサイトでのNTTドコモによる情報収集と利用について（NTTドコモ）</a><br/>5.　本条は、会員データの取扱いのうち本サービスに特有の内容を定めるものです。会員データの取扱いについては、本条の定めのほか、当社が別途定める<a className="c-popupMemberShip__sentenceTxtLink" href="https://m-78.jp/privacy/" target="_blank" rel="noreferrer">プライバシーポリシー</a>が適用されます。また、本アプリにより取得する会員データについては<a className="c-popupMemberShip__sentenceTxtLink" href="#privacy">「アプリケーション・プライバシーポリシー」</a>が、本サイトにより取得する会員データについては<a className="c-popupMemberShip__sentenceTxtLink" href="https://m-78.jp/cookie/" target="_blank" rel="noreferrer">「クッキーについて」</a>が、それぞれ適用されます。<br/><br/>第17条（本規約の変更）<br/>1.　当社は、次に掲げる場合には、個別に会員と合意をすることなく本規約及び本サービス利用契約の内容を変更することができるものとします。<br/>（1）本規約の変更が、会員の一般の利益に適合するとき。<br/>（2）本規約の変更が、本サービス利用契約をした目的に反せず、かつ、民法548条の4に定める変更の事情に照らして合理的なものであるとき。<br/>2.　前項の場合、本サービスの利用条件は、変更後の規約によるものとします。<br/>3.　当社は、本規約を変更する場合には予めその効力発生時期を定め、かつ、本規約を変更する旨及び変更後の本規約の内容並びにその効力発生時期について、本サイト及び本アプリを通じて公表するものとします。<br/>4.　会員は、本規約の変更の効力が生じた後に本サービスを利用した場合、変更後の本規約に同意したものとみなされます。変更後の本規約に同意できない場合、会員は本サービスを利用することはできません。会員は、常に最新の本規約及び補足条件等を確認した上で、本サービスを利用してください。<br/><br/>第18条（連絡及び通知）<br/>本サービスに関するお問い合わせその他会員から当社に対する連絡又は通知、及び本規約の変更に関する通知その他当社から会員に対する連絡又は通知は、当社の定める方法で行うものとします。<br/><br/>第19条（サービス利用契約上の地位の譲渡等）<br/>1.　会員は、当社の書面による事前の承諾なく、本サービス利用契約上の地位又は本規約に基づく権利義務につき、第三者に対して譲渡、承継、移転、貸与、担保設定その他の処分をすることはできません。<br/>2.　当社は本サービスに係る事業の全部又は一部を第三者に譲渡又は承継する場合があります。この場合、事業譲渡又は事業承継に伴って本サービス利用契約上の地位、本規約に基づく権利義務並びに会員の登録事項が、当該事業譲渡の譲受人に譲渡され又は承継されることとなります。会員は、本項に定める事業譲渡及び事業承継の可能性を予め理解のうえ、同意するものとします。<br/><br/>第20条（分離可能性）<br/>本規約のいずれかの条項の全部又はその一部が、消費者契約法その他の法令等により無効又は執行不能と判断された場合であっても、本規約の残部の規定及び一部が無効又は執行不能と判断された場合の規定の残部は、なお完全に効力を有するものとします。<br/><br/>第21条（準拠法及び管轄裁判所）<br/>本規約及び本サービス利用契約は日本法に準拠するものとし、本規約又は本サービス利用契約の内容に関して、当社と会員との間に生じる一切の紛争については、東京地方裁判所を第一審の専属的合意管轄裁判所とします。<br/><br/><br/><b className="c-static__center" id="privacy">アプリケーション・プライバシーポリシー</b><br/><br/>株式会社円谷プロダクション（以下、「当社」といいます。）は、当社が提供するスマートフォン用アプリケーション「TSUBURAYA IMAGINATIONアプリ」（以下、「本アプリ」といいます。）を通じて、利用者の利用者情報をご提供いただき、本アプリに関するサービス（以下、「本サービス」といいます。）の円滑な提供を実施させていただくために、アプリケーション・プライバシーポリシーを定め、利用者情報の保護に努めます。なお、利用者が本アプリによる利用者情報の提供をご希望されない場合は、本アプリ及び本サービスをご利用になれませんので、予めご了承ください。<br/>1.　利用者情報の取得と利用目的<br/>（１）当社が本アプリ経由で自動的に取得する利用者情報（以下、「利用者情報」といいます。）は以下のとおりです。
                                        <table className="c-staticTable">
                                            <tr className="c-staticTable__wrap">
                                                <th className="c-staticTable__ttl">利用する利用者情報</th>
                                                <th className="c-staticTable__ttl">利用者情報の利用目的</th>
                                            </tr>
                                            <tr className="c-staticTable__wrap">
                                                <td className="c-staticTable__item">・Cookie情報(識別ID)<br/>・本アプリの起動時に通知される端末固有ID<br/>・ご利用機種<br/>・会員の契約プラン及び会員ステータス
                                                <td className="c-staticTable__item">本サービス提供の際に利用者を識別するため/サービスの改善等を目的とした利用状況の分析をするため</td>
                                                </td>
                                            </tr>
                                            <tr className="c-staticTable__wrap">
                                                <td className="c-staticTable__item">各コンテンツへのアクセス履歴</td>
                                                <td className="c-staticTable__item">サービスの改善等を目的とした利用状況の分析をするため/広告運用のため</td>
                                            </tr>
                                            <tr className="c-staticTable__wrap">
                                                <td className="c-staticTable__item">広告配信用識別子：IDFA/AAID</td>
                                                <td className="c-staticTable__item">当社又は当社以外の事業者の商品及びサービスに関連する広告その他の情報の配信</td>
                                            </tr>
                                            <tr className="c-staticTable__wrap">
                                                <td className="c-staticTable__item">通知用トークン</td>
                                                <td className="c-staticTable__item">
                                                プッシュ通知機能の提供のため（２）当社は、当社が別に定める「プライバシーポリシー」その他利用者と当社の間の契約において別段の定めがある場合には、利用者情報を、これらに記載の利用目的の範囲で利用することがあります。<br/>（３）当社は、本アプリ及び本サービスの提供のために、本アプリ以外で当社が適正に取得した利用者の個人情報を利用することがあります。また、当社は利用者情報とこれらの個人情報とを組み合わせた上で、上記の利用目的の範囲で利用することがあります。</td>
                                            </tr>
                                        </table><br/>2．情報収集モジュールによる取得<br/>当社は、前条に記載の利用者情報を、本アプリ内に組み込まれた下記の情報収集モジュールにて取得します。情報収集モジュールとは、第三者が提供するプログラムであって、利用者情報を取得・解析するための機能をもつものをいいます。なお、当社は情報収集モジュールで取得した利用者情報を、利用者個人を識別するIDと組み合わせた上で、利用者の属性情報等を付加し、利用する場合があります。<br/><span className="c-static__itemMark">記</span>GoogleAnalytics, Firebase<br/>提供会社：Google LLC及びその完全子会社<br/>取得項目：利用者のアプリご利用状況<br/>プライバシーポリシー：<a className="c-popupMemberShip__sentenceTxtLink" href="https://www.google.com/intl/ja/policies/privacy/partners/" target="_blank" rel="noreferrer">https://www.google.com/intl/ja/policies/privacy/partners/</a><br/><br/>利用者情報はGoogleの管理するサーバシステムに格納されます。 なお、Googleは当該利用者情報を、Googleが定める利用目的の範囲で利用します。<br/><br/>3．利用者情報の第三者提供<br/>（１）当社は、本アプリにおいて取得した会員情報の一部の取扱いを、TSUBURAYA IMAGINATIONのマーケティング及び今後の新事業への活用のために株式会社NTTドコモに提供します。
                                        <div className="c-static__indent">（２）利用者情報は、前項又は以下の各号に定める場合を除き、第三者に送信され、又は提供されることはありません。<br/>①第2条に定める情報収集モジュール提供者へ送信する場合<br/>②当社との間の契約にて明示的に承諾を取得している場合<br/>③法令に基づく提供、業務委託に伴う委託先への提供等、個人情報保護上、本人の同意を得ることなく第三者への提供が認められる場合</div><br/><br/>4．利用者情報の取得・蓄積・利用に関する同意<br/>本アプリ及び本サービスは本アプリケーション・プライバシーポリシーをご確認いただき、内容を理解したうえでご利用ください。ご利用者は本アプリをインストールする際に、本アプリケーション・プライバシーポリシーをご確認ください。また、本アプリケーション・プライバシーポリシーは本アプリ内の<a className="c-popupMemberShip__sentenceTxtLink" href="#trems">利用規約</a>に公開いたします。<br/><br/>5．利用者情報の取得停止等<br/>（１）本アプリでは、利用者情報の取得、利用を停止する手段を提供しておりません。これらを停止したい場合には、本アプリをアンインストールしてください。 なお、アンインストール時点で既に取得済みの利用者情報については、当社は、第1条に定める範囲で引き続き利用することがあります。<br/>（２）利用者は、自己の保有するデバイスの設定を変更することによって、IDFA/AAIDの自動収集の停止及びIDFA/AAIDを利用した行動ターゲティング広告の無効化を行うことができます。自動収集の停止等を希望される場合は、Apple Inc.及びGoogle LLC各社の案内に従って設定を変更してください。<br/>IDFA（Apple Inc.）：<a className="c-popupMemberShip__sentenceTxtLink" href="https://support.apple.com/ja-jp/HT202074" target="_blank" rel="noreferrer">https://support.apple.com/ja-jp/HT202074</a><br/><br/>AAID（Google LLC）：<a className="c-popupMemberShip__sentenceTxtLink" href="https://support.google.com/googleplay/answer/3405269" target="_blank" rel="noreferrer">https://support.google.com/googleplay/answer/3405269</a><br/><br/>6．利用者情報の取り扱いに関する問い合わせ窓口<br/>本アプリ及び本サービスにおける利用者情報の取扱いに関して、ご意見・ご要望がございましたら、下記窓口までご連絡くださいますようお願いします。<br/><br/>■掲載箇所： TSUBURAYA IMAGINATION ＞ FAQ/問合わせ ＞ お問い合わせはこちら<br/>■URL：<a className="c-popupMemberShip__sentenceTxtLink" href="https://account.m-78.jp/Account/Home/ContactUs?redirecturl=https%3A%2F%2Fimagination.m-78.jp" target="_blank" rel="noreferrer">https://account.m-78.jp/Account/Home/ContactUs</a><br/><br/>7．本アプリケーション・プライバシーポリシーの変更<br/>当社は、本アプリのバージョンアップに伴って、本アプリケーション・プライバシーポリシーを変更することがあります。変更内容については、新バージョンのアプリケーションに付随するアプリケーション・プライバシーポリシーをご参照ください。<br/><br/>
                                        <div className="c-popupMemberShip__version">2021年3月15日制定　Ver.1.0<br/>2021年7月29日改訂　Ver.1.1<br/>2023年2月20日改訂　Ver.1.2</div>
                                    </div>
                                </div>
                            </div>
                        }
                        {/* 新規登録時に表示. */}
                        {plan.isNone(memberPlan) &&
                            <div className="c-popupMemberShipCheck">
                                <label className="c-checkbox c-popupMemberShipCheck__checkBox">
                                    <input
                                        className="c-checkbox-input"
                                        type="checkbox"
                                        name="checkbox[]"
                                        onChange={handleCheckBox}
                                        disabled={!isBottom}
                                    />
                                        <span className="c-checkbox-parts is-disabled"></span>
                                </label>
                                <div className="c-checkboxTxt">TSUBURAYA IMAGINATION 会員規約に同意する</div>
                            </div>
                        }
                        <div className="c-btnWrap p-popupBoxWrap__btnWrap p-popupBoxWrap__btnWrap--plan">
                            {!isNotJoin && <div className="c-btn c-btnWrap__size c-btn--gray" onClick={back}>もどる</div>}
                            {isNotJoin && <div className="c-btn c-btnWrap__size c-btn--gray" onClick={cancelJoin}>入会しない</div>}
                            {canPushNext() && <div className="c-btn c-btnWrap__size c-btn--thinRed" onClick={() => purchaseCheck()}>続ける</div>}
                            {!canPushNext() && <div className="c-btn c-btnWrap__size c-btn--thinRed is-color">続ける</div>}
                        </div>
                    </div>
                </div>
            </div>
            <div className="p-popup__bg"></div>
        </div>
    );
};

export default PlanSelect;
